import { Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { MsalProvider } from "@azure/msal-react";
import { AuthProvider } from "./components/auth/AuthContext";
import { SplashScreen } from "./components/splash-screen";
import { router } from "./routes";
import { msalInstance } from "./utils/mlsa";
import { errorStyles, successStyles } from "./utils/functions";
import { SEOHead } from "src/components/seo-component";
import "react-tooltip/dist/react-tooltip.css";
import "react-datepicker/dist/react-datepicker.css";
import "react-confirm-alert/src/react-confirm-alert.css";
import "./index.css";

/* -------------------- React Query -------------------- */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 2 * 60,
      cacheTime: 1000 * 10 * 60,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/* -------------------- Buffer Fix (MSAL / Crypto) -------------------- */
global.Buffer = global.Buffer || require("buffer").Buffer;

/* -------------------- Version Check -------------------- */
const checkForNewVersion = async () => {
  try {
    const response = await fetch("/version.txt", { cache: "no-store" });
    const latestVersion = (await response.text()).replace("Build:", "").trim();
    const storedVersion = localStorage.getItem("app_version");
    // First visit — store version, no reload
    if (!storedVersion) {
      localStorage.setItem("app_version", latestVersion);
      return;
    }
    // New version detected — reload once
    if (storedVersion !== latestVersion) {
      localStorage.setItem("app_version", latestVersion);
      window.location.reload();
    }
  } catch (error) {
    console.error("Version check failed:", error);
  }
};

/* -------------------- App Bootstrap -------------------- */
function AppBootstrap() {
  useEffect(() => {
    checkForNewVersion();
  }, []);
  return <RouterProvider router={router} />;
}

/* -------------------- Render -------------------- */
const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <AuthProvider>
    <QueryClientProvider client={queryClient}>
      <MsalProvider instance={msalInstance}>
        <Toaster
          position="top-right"
          reverseOrder={false}
          toastOptions={{
            success: successStyles,
            error: errorStyles,
          }}
        />
        <Suspense fallback={<SplashScreen />}>
          <SEOHead />
          <AppBootstrap />
        </Suspense>
      </MsalProvider>
    </QueryClientProvider>
  </AuthProvider>,
);
